import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { IMAGES } from "../data/site";
import { MaskedLine } from "./Reveal";
import { useI18n } from "../i18n/LanguageContext";

export const Hero = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative h-[100svh] overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Massif de Belledonne enneigé, station Les 7 Laux Le Pleynet"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-pine-dark/60 via-black/30 to-pine-dark/70" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-28 md:pb-32">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="text-xs uppercase tracking-[0.3em] text-wood-light font-semibold mb-6"
        >
          {t("hero.overline")}
        </motion.p>

        <h1 className="font-serif text-cream tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-8xl">
          <MaskedLine delay={0.15}>{t("hero.line1")}</MaskedLine>
          <MaskedLine delay={0.3}>
            <span className="italic text-wood-light">{t("hero.line2a")}</span>
            {t("hero.line2b")}
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 max-w-xl text-cream/80 text-base md:text-lg leading-relaxed"
        >
          {t("hero.intro")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link
            to="/studio/glaciers"
            data-testid="hero-cta-studios"
            className="group flex items-center gap-3 bg-cream text-pine px-7 py-4 text-sm font-semibold tracking-wide hover:bg-wood hover:text-pine-dark transition-colors duration-300"
          >
            {t("hero.ctaStudios")}
            <ArrowUpRight aria-hidden className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-cta-contact"
            className="group flex items-center gap-3 border border-cream/50 text-cream px-7 py-4 text-sm font-semibold tracking-wide hover:bg-cream/10 transition-colors duration-300"
          >
            {t("hero.ctaContact")}
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-cream/60"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
          <ArrowDown aria-hidden className="w-5 h-5" />
        </motion.div>
      </motion.div>
    </section>
  );
};
